import type { ReactNode } from 'react';
import { formatCurrency } from '@/react/utils/formatters';
import type { YieldData } from './types';
import { useTranslation } from '@hooks/useTranslation';

interface OrderYieldCardProps {
  /** Absent on an order with nothing to total yet; the left column then empties. */
  yieldData?: YieldData | null;
  /**
   * The Attio row, which takes this row's right column. Passed in rather than
   * rendered here so this component keeps knowing only about yield.
   */
  attio?: ReactNode;
}

export function OrderYieldCard({ yieldData, attio }: OrderYieldCardProps) {
  const { t } = useTranslation();
  const formatPercentage = (percentage: number) => `${percentage.toFixed(2)}%`;

  const isZeroYield = yieldData?.percentage === 0;

  return (
    // Same grid as the rows above, so the figures keep their column width.
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
      <div className="space-y-4">
        {yieldData && (
          <>
        <div className="flex items-center gap-4">
          <h3 className="font-inter text-lg font-semibold leading-normal text-off-black">
            {t('yield_card.title')}
          </h3>
          <div
            className={`flex items-center justify-center rounded-md border px-2 py-1 ${isZeroYield ? 'border-chilli-red bg-chilli-red/10' : 'border-yield-positive bg-yield-positive/10'
              }`}
          >
            <span
              className={`font-inter text-xs font-semibold leading-tight ${isZeroYield ? 'text-chilli-red' : 'text-confirmed-green'
                }`}
            >
              {formatPercentage(yieldData?.percentage ?? 0)}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-1.5 md:flex-row md:gap-8">
          <p className="font-inter text-sm font-normal leading-tight text-off-black">
            {t('yield_card.revenue')}: {formatCurrency(yieldData?.revenue)}
          </p>
          <p className="font-inter text-sm font-normal leading-tight text-off-black">
            {t('yield_card.food_cost')}: {formatCurrency(yieldData?.food_cost)}
          </p>
          <p className="font-inter text-sm font-normal leading-tight text-off-black">
            {t('yield_card.ops_manpower')}: {formatCurrency(yieldData?.ops_manpower)}
          </p>
        </div>
        </>
        )}
      </div>

      <div className="space-y-4">{attio}</div>
    </div>
  );
}
